import type { IdentityNamespaceKind } from '@ofmos/shared/identity';
import type {
  CreateNamespaceInput,
  DetectorState,
  DuplicateEvidence,
  DuplicateGuard,
  FieldValue,
  IdentityClient,
  IdentityElsewhereHit,
  IdentityKeyEntry,
  IdentityNamespace,
  IdentityPanel,
  MergeFieldDecision,
  MergePreflight,
  MergeRecord,
  MergeRepoint,
  PendingChangeSummary,
  PossibleDuplicate,
  UnmergePreflight,
  UnmergeResult,
  UpdateNamespaceInput,
} from '../client';
import { toActor, toFieldValue } from './mapping';
import { toPendingChangeSummary } from './pendingClient';
import { ApiError, type Transport } from './transport';
import type { WirePendingChange } from './wire';

/**
 * `IdentityClient` over `/api/identity/*` (docs/decisions/identity-resolution.md).
 *
 * Thin, like the pending client and for the same reason: every DECISION in this module
 * lives on the server - which guard demoted a pair, which value survives a merge, what
 * an unmerge can and cannot put back. A second opinion computed in the browser would
 * go stale the moment a feeder wrote, and this is the one module where a stale opinion
 * fuses two humans.
 *
 * The one thing that is not thin is the UNMERGE, and that is deliberate: it follows the
 * INV-8 two-call pattern the metadata client established, so a refusal that hands back
 * a confirm token is retried once with it rather than surfacing as an error the user
 * cannot act on.
 */

interface WireNamespace {
  readonly id: string;
  readonly key: string;
  readonly label: string;
  readonly kind: IdentityNamespaceKind;
  readonly defaultRegion: string | null;
  readonly description: string | null;
  readonly keyCount: number;
  readonly fieldCount: number;
}

interface WireIdentityKey {
  readonly namespaceId: string;
  readonly namespaceKey: string;
  readonly namespaceLabel: string;
  readonly fieldId: string;
  readonly valueNorm: string;
  readonly valueRaw: string;
  readonly isCurrent: boolean;
}

interface WireElsewhereHit extends Omit<WireIdentityKey, 'fieldId' | 'valueRaw' | 'namespaceKey'> {
  readonly objectId: string;
  readonly recordId: string;
  readonly label: string;
}

interface WireDuplicate {
  readonly recordId: string;
  readonly otherRecordId: string;
  readonly evidence: readonly DuplicateEvidence[];
  readonly guard: DuplicateGuard;
  readonly reason: string;
}

interface WireDecision extends Omit<MergeFieldDecision, 'value' | 'winnerValue' | 'loserValue'> {
  readonly value: unknown;
  readonly winnerValue: unknown;
  readonly loserValue: unknown;
}

interface WireMergePreflight {
  readonly objectId: string;
  readonly winnerRecordId: string;
  readonly loserRecordId: string;
  readonly decisions: readonly WireDecision[];
  readonly payload: Readonly<Record<string, unknown>>;
  readonly contestedFieldIds: readonly string[];
  readonly repoints: readonly MergeRepoint[];
  readonly totalRepointed: number;
  readonly refusal: string | null;
  readonly unmergePartial: boolean;
  readonly fingerprint: string;
}

interface WireMerge {
  readonly id: string;
  readonly objectId: string;
  readonly winnerRecordId: string;
  readonly loserRecordId: string;
  readonly decidedBy: Parameters<typeof toActor>[0];
  readonly decidedByName?: string | null;
  readonly decidedAt: string;
  readonly unmergePartial: boolean;
  readonly undoneAt: string | null;
}

function toNamespace(namespace: WireNamespace): IdentityNamespace {
  return {
    id: namespace.id,
    key: namespace.key,
    label: namespace.label,
    kind: namespace.kind,
    defaultRegion: namespace.defaultRegion,
    description: namespace.description,
    /**
     * NO `?? 0` DEFAULT. The server not sending these is a contract break, and a
     * fallback turns it into "0 values on 0 fields" - a plausible, wrong answer on the
     * one screen an operator opens to find out whether the key index is stale. It
     * shipped exactly that way once.
     */
    keyCount: namespace.keyCount,
    fieldCount: namespace.fieldCount,
  };
}

function toValues(values: Readonly<Record<string, unknown>>): Record<string, FieldValue> {
  const mapped: Record<string, FieldValue> = {};
  for (const [fieldId, raw] of Object.entries(values)) mapped[fieldId] = toFieldValue(raw);
  return mapped;
}

function toDecision(decision: WireDecision): MergeFieldDecision {
  return {
    ...decision,
    value: toFieldValue(decision.value),
    winnerValue: toFieldValue(decision.winnerValue),
    loserValue: toFieldValue(decision.loserValue),
  };
}

export class HttpIdentityClient implements IdentityClient {
  constructor(private readonly transport: Transport) {}

  async listNamespaces(): Promise<readonly IdentityNamespace[]> {
    const response = await this.transport.request<{ namespaces: readonly WireNamespace[] }>({
      method: 'GET',
      path: '/api/identity/namespaces',
    });
    return response.namespaces.map(toNamespace);
  }

  async createNamespace(input: CreateNamespaceInput): Promise<IdentityNamespace> {
    const response = await this.transport.request<{ namespace: WireNamespace }>({
      method: 'POST',
      path: '/api/identity/namespaces',
      body: {
        key: input.key,
        label: input.label,
        kind: input.kind,
        ...(input.defaultRegion === undefined ? {} : { defaultRegion: input.defaultRegion }),
        ...(input.description === undefined ? {} : { description: input.description }),
      },
    });
    return toNamespace(response.namespace);
  }

  /** Only `label` and `description` are sent: the other three are fixed at creation (1.3). */
  async updateNamespace(
    namespaceId: string,
    input: UpdateNamespaceInput,
  ): Promise<IdentityNamespace> {
    const response = await this.transport.request<{ namespace: WireNamespace }>({
      method: 'PATCH',
      path: `/api/identity/namespaces/${namespaceId}`,
      body: {
        label: input.label,
        ...(input.description === undefined ? {} : { description: input.description }),
      },
    });
    return toNamespace(response.namespace);
  }

  async deleteNamespace(namespaceId: string): Promise<void> {
    await this.transport.request<null>({
      method: 'DELETE',
      path: `/api/identity/namespaces/${namespaceId}`,
    });
  }

  async getRecordIdentity(objectId: string, recordId: string): Promise<IdentityPanel> {
    const response = await this.transport.request<{
      keys: readonly WireIdentityKey[];
      elsewhere: readonly WireElsewhereHit[];
    }>({ method: 'GET', path: `/api/identity/records/${objectId}/${recordId}` });
    return {
      objectId,
      recordId,
      keys: response.keys.map((key): IdentityKeyEntry => ({ ...key })),
      elsewhere: response.elsewhere.map(
        (hit): IdentityElsewhereHit => ({
          objectId: hit.objectId,
          recordId: hit.recordId,
          label: hit.label,
          namespaceId: hit.namespaceId,
          namespaceLabel: hit.namespaceLabel,
          valueNorm: hit.valueNorm,
          isCurrent: hit.isCurrent,
        }),
      ),
    };
  }

  async recordDuplicates(
    objectId: string,
    recordId: string,
  ): Promise<readonly PossibleDuplicate[]> {
    const response = await this.transport.request<{ duplicates: readonly WireDuplicate[] }>({
      method: 'GET',
      path: `/api/identity/records/${objectId}/${recordId}/duplicates`,
    });
    return response.duplicates.map((entry) => ({ objectId, ...entry }));
  }

  async objectDuplicates(objectId: string): Promise<readonly PossibleDuplicate[]> {
    const response = await this.transport.request<{ duplicates: readonly WireDuplicate[] }>({
      method: 'GET',
      path: `/api/identity/objects/${objectId}/duplicates`,
    });
    return response.duplicates.map((entry) => ({ objectId, ...entry }));
  }

  async mergePreflight(input: {
    readonly objectId: string;
    readonly winnerRecordId: string;
    readonly loserRecordId: string;
  }): Promise<MergePreflight> {
    const response = await this.transport.request<WireMergePreflight>({
      method: 'POST',
      path: '/api/identity/merges/preflight',
      body: input,
    });
    return {
      ...response,
      decisions: response.decisions.map(toDecision),
      payload: toValues(response.payload),
    };
  }

  async proposeMerge(input: {
    readonly objectId: string;
    readonly recordA: string;
    readonly recordB: string;
  }): Promise<PendingChangeSummary> {
    const response = await this.transport.request<{ pendingChange: WirePendingChange }>({
      method: 'POST',
      path: '/api/identity/merges/propose',
      body: input,
    });
    return toPendingChangeSummary(response.pendingChange);
  }

  async listMerges(filter: {
    readonly recordId?: string;
    readonly objectId?: string;
  }): Promise<readonly MergeRecord[]> {
    const response = await this.transport.request<{ merges: readonly WireMerge[] }>({
      method: 'GET',
      path: '/api/identity/merges',
      query: {
        ...(filter.recordId === undefined ? {} : { recordId: filter.recordId }),
        ...(filter.objectId === undefined ? {} : { objectId: filter.objectId }),
      },
    });
    return response.merges.map((merge) => ({
      id: merge.id,
      objectId: merge.objectId,
      winnerRecordId: merge.winnerRecordId,
      loserRecordId: merge.loserRecordId,
      decidedBy: toActor(merge.decidedBy, merge.decidedByName),
      decidedAt: merge.decidedAt,
      unmergePartial: merge.unmergePartial,
      undoneAt: merge.undoneAt,
    }));
  }

  unmergePreflight(mergeId: string): Promise<UnmergePreflight> {
    return this.transport.request<UnmergePreflight>({
      method: 'GET',
      path: `/api/identity/merges/${mergeId}/unmerge-preflight`,
    });
  }

  /**
   * The INV-8 destructive flow, in two calls, exactly as `metadataClient.destructive`
   * does it: go out with whatever token the caller was shown, and if the server refuses
   * with `confirmation_required` because that token is not the one it wants, read the
   * real token off the refusal and repeat once. The user still confirmed - the dialog
   * is what produced the call - and the token keeps its meaning: proof that the caller
   * was shown THIS plan.
   */
  async unmerge(mergeId: string, confirmToken?: string): Promise<UnmergeResult> {
    const path = `/api/identity/merges/${mergeId}/unmerge`;
    try {
      return await this.transport.request<UnmergeResult>({
        method: 'POST',
        path,
        body: confirmToken === undefined ? {} : { confirmToken },
      });
    } catch (error) {
      if (!(error instanceof ApiError) || error.code !== 'confirmation_required') throw error;
      const token = error.confirmToken;
      if (token === undefined || token === confirmToken) throw error;
      return await this.transport.request<UnmergeResult>({
        method: 'POST',
        path,
        body: { confirmToken: token },
      });
    }
  }

  getDetector(): Promise<DetectorState> {
    return this.transport.request<DetectorState>({
      method: 'GET',
      path: '/api/identity/detector',
    });
  }

  setDetector(paused: boolean): Promise<DetectorState> {
    return this.transport.request<DetectorState>({
      method: 'PUT',
      path: '/api/identity/detector',
      body: { paused },
    });
  }
}
